import { Widget } from '../../core/Widget.js';
import { DomHelpers } from '../../core/DomHelpers.js';
import { SelectionModel } from '../../core/selection/SelectionModel.js';
import { TableSelectionAdapter } from './TableSelectionAdapter.js';
import { CmdBus, CmdPanelWidget } from '../CmdPanelWidget.js';
import { SortWidget } from '../../search-core/SortWidget.js';
import { FilterWidget } from '../../search-core/FilterWidget.js';
import { TabsPanelWidget } from '../TabsPanelWidget.js';
import { DialogComponent } from '../../components/DialogComponent/DialogComponent.js';
import { ImSys } from '../../core/ImSys.js';
import { Validation } from '../../core/Validation.js';
import { encodeValues, emptyGuid } from '../../core/EncodeValues.js';
import { Loc } from '../../locales/registry.js';
import { HiddenStateSync } from '../../core/selection/HiddenStateSync.js';
import { TableFilterAdapter } from './TableFilterAdapter.js';
import { TableStackAdapter } from './TableStackAdapter.js';
import { TABLE_NS, TableServerCmd, getTpk, writeTpk, TableServerEvents, readTpk } from './ITableWidget.js';
import { ItemsCmdController } from '../../core/items/ItemsCmdController.js';
import { TableItemsController } from './TableItemsController.js';
import { TableUiCommandProvider } from './TableUiCommandProvider.js';
import { ItemsUiCommandProvider } from '../../core/items/ItemsUiCommandProvider.js';
import { TableServerCmdProvider } from './TableServerCmdProvider.js';
import { ViewWidget } from '../../view/ViewWidget.js';
import { ViewMode } from '../../view/ViewOptions.js';
import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';
import { ViewCmdType } from '../../view/IViewWidget.js';
import { confirmAsideReplace } from '../../core/tools/ModalAside.js';

// src/widgets/table/TableWidget.ts
/**
 * Виджет многофункциональной таблицы данных
 */
class TableWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Просмотр, редактированеи, вставка */
        this.mode = "view";
        this.isTarget = false; // фокус таблицы для клавиатурного управления
        this.isRowFolder = false;
        this.cellClickPending = false;
        this.dispose = [];
    }
    /** Регистрация статик провайдера  */
    static _uiCommandProviders() {
        const itemsProvider = new ItemsUiCommandProvider({
            namespace: TABLE_NS,
            priority: 5,
            defaultNSAllowed: false,
            id: 'TableWidgetItems'
        });
        return [new TableUiCommandProvider(), itemsProvider];
    }
    /** Серверные провайдеры команд для таблицы */
    static _serverCommandProviders() {
        return new TableServerCmdProvider();
    }
    static _cssKey() {
        return 'im-table';
    }
    /** Ключ таблицы */
    get tableKey() {
        return this._tableKey;
    }
    /** Доступ к командной панели */
    get panel() {
        return this.cmdPanel;
    }
    _create(op) {
        this.options = op;
        this._tableKey = this.options.tablekey;
        this.cellClickCommand = this.resolveCellClickCommand(this.options.oncellclick);
        this.hiddenSync = new HiddenStateSync(this.element, this._tableKey);
        // Базовые биндинги/сортировка/селект
        this.initSelection();
        this.initSorting();
        this.bindBasics();
        // 2026-09-21: подписи ячеек и карточный режим на узкой ширине
        this.stackAdapter = new TableStackAdapter(this.element, () => this.setTablePagination());
        // Констроллер строк таблицы
        this._itemsCtrl = new TableItemsController(this);
        /** Создание контроллера таблицы */
        this.items = new ItemsCmdController({
            selection: this._itemsCtrl,
            crud: this._itemsCtrl,
            order: this._itemsCtrl,
            folders: this._itemsCtrl
        });
        // Лоадер и панель создаём ТОЛЬКО если это не notoolstrip и не hidecontrols
        const noToolstrip = this.element.classList.contains("im-table-notoolstrip");
        const controlsHidden = this.options.hidecontrols === true;
        if (!noToolstrip && !controlsHidden) {
            this.ensureLoader(); // гарантируем .loading-container до любых showLoading()
            this.initCmdPanel(); // создаём панель
        }
        // Инициализация
        this.refreshState("view");
        // scroll-area (лениво)
        this.scrollArea = this.element.querySelector(".scroll-area") || undefined;
        // Отложенная загрузка
        if (this.element.classList.contains("onloading")) {
            this.showLoading();
            const run = () => this._itemsCtrl.reloadAll();
            // сначала дождёмся layout, затем ещё один макротик — как в jQuery-legacy, но чуть стабильнее
            if ("requestAnimationFrame" in window) {
                requestAnimationFrame(() => setTimeout(run, 0));
            }
            else {
                setTimeout(run, 0);
            }
        }
        // автооткрытие формы по смене _tpk_*__currentRow
        if (this.options.asidereload === true) {
            const cr = this.element.querySelector(`input[data-key=_tpk_${this._tableKey}__currentRow]`);
            let prev = '';
            cr?.addEventListener('change', () => {
                const v = cr.value || '';
                if (!v || v === prev)
                    return;
                const back = prev;
                prev = v;
                // 2026-09-14: отказ заменить карточку с несохранёнными данными возвращает текущую строку
                // к строке открытой карточки; change от возврата совпадёт с prev и форму не откроет.
                // Через модель, а не setCurrentById: клик по кнопке диалога снимает с таблицы
                // признак активной (isTarget), и адаптер выбор бы не принял
                this.editRow(false, false, () => {
                    if (!back || !this.element.querySelector(`tbody tr[data-id="${CSS.escape(back)}"]`))
                        return;
                    prev = back;
                    this.selection.set([back]);
                });
            });
            // 2026-09-14: клик по уже текущей строке не меняет currentRow, и change не приходит.
            // Если карточку этой строки закрыли или панель заняла другая форма — открыть её снова.
            const onCurrentRowClick = (e) => {
                if (e.detail > 1 || e.ctrlKey || e.metaKey || e.shiftKey)
                    return;
                if (this.mode !== "view" || !this._asideRowView || this._asideRowView.isConnected)
                    return;
                const target = e.target;
                const tr = target?.closest("tbody tr:not(.h)");
                if (!tr || tr !== this.currentRow() || !prev || tr.dataset.id !== prev)
                    return;
                // Выбор ячейки, иконка отметки и интерактивные элементы строки карточку не открывают
                if (target.closest("td[data-cellclick], .r-ic, a, button, input, select, textarea, label, [contenteditable], [role='button'], [data-imcmd]"))
                    return;
                this.editRow();
            };
            this.element.addEventListener("click", onCurrentRowClick);
            this.dispose.push(() => this.element.removeEventListener("click", onCurrentRowClick));
        }
        // Респонсив пересчёт пагинации панели (если есть панель)
        if (this.element.querySelector('.im-toolstrip__pagination')) {
            const onResize = () => this.setTablePagination();
            window.addEventListener('resize', onResize);
            this.dispose.push(() => window.removeEventListener('resize', onResize));
            // если есть .im-panel — подписаться на 'tabClicked' у панели
            const panel = this.element.closest('.im-panel');
            panel?.addEventListener('tabClicked', onResize);
            this.dispose.push(() => panel?.removeEventListener('tabClicked', onResize));
        }
    }
    // ──────────────────────────────────────────────────────────────────────────
    // Инициализация подсистем
    // ──────────────────────────────────────────────────────────────────────────
    /** Убедиться, что у таблицы есть контейнер загрузки */
    ensureLoader() {
        // уже есть → ничего не делаем
        if (this.element.querySelector(".loading-container")) {
            return;
        }
        const lc = document.createElement("div");
        lc.className = "loading-container";
        lc.innerHTML = `<span class="loading ui-corner-all">${Loc.dataLoading}</span>`;
        this.element.prepend(lc);
    }
    initSelection() {
        this.selection = new SelectionModel({
            mode: "multiple",
            keepOnReload: true,
            isSelectable: (id) => {
                const tr = this.getRow(id);
                return !!tr && !tr.classList.contains("ar");
            },
        });
        // Право на выбор строки есть если:
        //  - явно задано 's', ИЛИ
        //  - есть возможность редактирования (e/r/f) — формой или inline
        const canSelectByRights = this.allows("edit") || // 'e'
            this.allows("rowEdit") || // 'r'
            this.allows("formEdit"); // 'f'
        const isEnabled = () => ((this.options.allow ?? "").includes("s") || canSelectByRights) &&
            this.element.classList.contains("_curtable") &&
            this.isTarget === true &&
            this.mode === "view" &&
            !this.cellClickPending &&
            !this.element.querySelector(".loading-container.on");
        this.selAdapter = new TableSelectionAdapter(this.selection, {
            root: this.element,
            tableKey: this._tableKey,
            isEnabled,
            onSelectionChange: (ids, currentId, reason) => {
                // isRowFolder влияет на refreshState
                if (currentId) {
                    const row = this.getRow(currentId);
                    this.isRowFolder = !!row?.querySelector(".r-ic.group");
                }
                else {
                    this.isRowFolder = false;
                }
                // синхронизируем панель
                this.cmdPanel?.setSelection?.(ids.length);
                // Меняем состояние hidden
                this.hiddenSync.write(ids, currentId, reason === 'cell');
                // легаси-коллбеки
                if (reason === 'row') {
                    this.emitCheckedChanged(ids);
                }
                // обновить состояние панели
                this.refreshState();
            },
            onCellClick: this.cellClickCommand
                ? (payload) => this.execCellClick(payload)
                : undefined,
            isCellClickPending: () => this.cellClickPending,
            sync: this.hiddenSync
        });
        this.selAdapter.bind();
    }
    initCmdPanel() {
        // Хост для панели перед таблицей
        const host = document.createElement("div");
        host.className = "im-toolstrip";
        this.element.prepend(host);
        this.cmdBus = new CmdBus();
        // Базовые
        this.cmdBus.on("reload", () => this._itemsCtrl.reloadAll(Array.from(this.selection?.selected || [])));
        this.cmdBus.on("save", () => this.saveRow());
        this.cmdBus.on("cancel", () => this.cancelEdit());
        // CRUD
        this.cmdBus.on("edit", () => this.editRow(false, true));
        this.cmdBus.on("insert", () => this.insertRow(false));
        this.cmdBus.on("insertCopy", () => this.insertRow(true));
        // Удаления/метки
        this.cmdBus.on("delMark", () => this.doDelMark(), { text: Loc.alertDelMarkRows, title: Loc.askDelMarkRows });
        this.cmdBus.on("delete", () => this._itemsCtrl.deleteSelected(), { text: Loc.alertDeleteRows, title: Loc.askDeleteRows });
        this.cmdBus.on("deleteFolder", () => this.doDeleteFolder(), { text: Loc.alertDeleteFolders, title: Loc.askDeleteFolders });
        // Группы
        this.cmdBus.on("editGroup", () => this.editGroup());
        this.cmdBus.on("insertGroup", () => this.insertGroup());
        this.cmdBus.on("changeGroup", () => this.changeGroup());
        // Буфер/перемещения
        this.cmdBus.on("rowsMoveUp", () => this._itemsCtrl.moveUp());
        this.cmdBus.on("rowsMoveDown", () => this._itemsCtrl.moveDown());
        this.cmdBus.on("copy", () => this._itemsCtrl.execRowsCmd("copy-rows"));
        this.cmdBus.on("cut", () => this._itemsCtrl.execRowsCmd("cut-rows"));
        this.cmdBus.on("paste", () => {
            this.showLoading();
            // execRowsCmd использовать нельзя, т.к. вызов без строк
            ViewWidgetHelper.ExecCmd(this.element, {
                cmd: TableServerCmd.paste_rows,
                onSuccess: () => this._itemsCtrl.reloadAll(),
                onError: () => { console.error('request error'); this.hideLoading(); }
            });
        });
        // Фильтр
        this.cmdBus.on("openFilter", () => this.openFilterCmd());
        this.cmdBus.on("closeFilter", () => this.closeFilterCmd());
        // Кастомные
        this.cmdBus.on("custom", ({ source }) => this.execCustomBtn(source));
        // Пагинация
        const curp = getTpk(this.element, this._tableKey, "curPage");
        const maxRows = getTpk(this.element, this._tableKey, "maxRows");
        this.cmdBus.on("navPrev", () => {
            this._onPagerChanged();
        });
        this.cmdBus.on("navNext", () => {
            this._onPagerChanged();
        });
        this.cmdBus.on("goPage", () => {
            this._onPagerChanged();
        });
        this.cmdBus.on("setMaxRows", () => {
            writeTpk(this.element, this._tableKey, "maxRows", this.cmdPanel?.getMaxRows()?.toString() ?? '');
            this.items.crud?.reloadAll(Array.from(this.selection?.selected || []));
        });
        const hasGroupInput = !!getTpk(this.element, this._tableKey, "groupID");
        const customItemsSource = this.element.querySelector(".toolstrip-custom-items");
        this.cmdPanel = CmdPanelWidget.getOrInit(host, {
            cmdBus: this.cmdBus,
            allow: (this.options.allow || "").trim(),
            filterButton: true,
            pagination: !!curp,
            maxRowsSelector: maxRows ? Array.from(new Set([parseInt(maxRows.value || "100", 10), 100, 500, 1000, 5000])) : undefined,
            addMissingButtons: true,
            customItemsSource: customItemsSource || undefined,
            supportsGroups: hasGroupInput,
        });
        // Первичная синхронизация
        this.refreshState();
        // Подписки, влияющие на панель
        if (curp) {
            const onCurpChange = () => this.refreshState();
            curp.addEventListener("change", onCurpChange);
            this.dispose.push(() => curp.removeEventListener("change", onCurpChange));
        }
        this.element.addEventListener("im-table-group-opened", () => this.refreshState());
    }
    _onPagerChanged() {
        writeTpk(this.element, this._tableKey, "curPage", this.cmdPanel?.getPage().toString() ?? '1');
        this.items.crud?.reloadAll();
    }
    initSorting() {
        const head = this.element.querySelector("thead");
        if (head && this.element.querySelector("._tsc")) {
            this.sortWidget = SortWidget.getOrInit(head);
        }
    }
    /** Помечает текущую таблицу (как в легаси) */
    currentTable(table) {
        // снять _curtable со всех таблиц
        document.querySelectorAll('.im-table._curtable')
            .forEach(t => t.classList.remove('_curtable'));
        // если это именно наш host — поставить флаг
        if (table === this.element) {
            this.element.classList.add('_curtable');
        }
    }
    bindBasics() {
        if (this.element.classList.contains('_curtable')) {
            this.isTarget = true;
        }
        // Активация текущей таблицы по mousedown
        const onActivate = () => {
            this.isTarget = true;
            this.currentTable(this.element);
        };
        this.element.addEventListener("mousedown", onActivate);
        this.dispose.push(() => this.element.removeEventListener("mousedown", onActivate));
        // Сброс флага вне таблицы
        const onDocDown = (e) => {
            if (!e.target?.closest?.(".im-table"))
                this.isTarget = false;
        };
        document.addEventListener("mousedown", onDocDown);
        this.dispose.push(() => document.removeEventListener("mousedown", onDocDown));
        // Даблклик по строке (без собственной логики выбора!)
        const onDblClick = (e) => {
            const tr = e.target.closest("tbody tr:not(.h)");
            if (!tr || tr.classList.contains("ar"))
                return;
            // При наличии прав — редактируем; иначе, если группа — открываем
            if (this.allows("edit")) {
                if (this.currentRow()?.classList.contains("_editrow")) {
                    this.cancelEdit();
                }
                if (tr.querySelector(".r-ic.group"))
                    this.openGroup();
                else if (this.mode === "view") {
                    this.editRow(false, true);
                }
            }
            else if (tr.querySelector(".r-ic.group")) {
                this.openGroup();
            }
        };
        this.element.addEventListener("dblclick", onDblClick);
        this.dispose.push(() => this.element.removeEventListener("dblclick", onDblClick));
    }
    allows(kind) {
        const a = this.options.allow || "";
        switch (kind) {
            case "edit": return a.includes("e");
            case "insert": return a.includes("i");
            case "delete": return a.includes("d");
            case "formEdit": return a.includes("f");
            case "rowEdit": return a.includes("r");
        }
    }
    // ──────────────────────────────────────────────────────────────────────────
    // Публичное API
    // ──────────────────────────────────────────────────────────────────────────
    /**
     * @param onCancel 2026-09-14: отказ заменить форму с несохранёнными данными в общей aside
     */
    editRow(formMode = false, viewEditMode = false, onCancel) {
        const row = this.currentRow();
        if (!row)
            return;
        const preferForm = this.isFormModePreferred();
        const doForm = this.allows("formEdit") && (!this.allows("rowEdit") || preferForm) || formMode;
        // 2026-09-14: форма строки заменит содержимое общей aside — спросить до запроса к серверу
        if (doForm)
            confirmAsideReplace(() => this.execEditRow(row, doForm, viewEditMode), onCancel);
        else
            this.execEditRow(row, doForm, viewEditMode);
    }
    execEditRow(row, doForm, viewEditMode) {
        this.showLoading();
        const options = { rowid: row.dataset.id };
        const cmd = doForm ? TableServerCmd.start_edit_form : TableServerCmd.start_edit;
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd,
            options,
            onSuccess: (data) => {
                if (doForm)
                    this.editRowFormComplete(row, data, viewEditMode);
                else
                    this.editRowInlineComplete(row, data?.html);
                this.hideLoading();
            },
            onError: () => { console.error('request error'); this.hideLoading(); }
        });
    }
    insertRow(copy = false) {
        const preferForm = this.isFormModePreferred();
        // 2026-09-14: форма вставки заменит содержимое общей aside — спросить до запроса к серверу
        if (preferForm)
            confirmAsideReplace(() => this.execInsertRow(copy, preferForm));
        else
            this.execInsertRow(copy, preferForm);
    }
    execInsertRow(copy, preferForm) {
        this.showLoading();
        const options = {};
        if (copy)
            options.rowid = this.currentRow()?.dataset.id;
        const cmd = preferForm ? TableServerCmd.get_insert_form : TableServerCmd.get_insert_row;
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd,
            options,
            onSuccess: (data) => {
                if (preferForm)
                    this.insertRowFormComplete(data);
                else
                    this.insertRowInlineComplete(data?.html);
                this.hideLoading();
            },
            onError: () => {
                console.error('request error');
                this.hideLoading();
            }
        });
    }
    saveRow() {
        const edit = this.element.querySelector("tbody tr._editrow");
        if (!edit)
            return;
        // строку с ошибками проверки полей на сервер не отправляем | 2026-10-06
        const invalidMessage = this._editRowValidationError(edit);
        if (invalidMessage !== null) {
            ImSys.alert(Loc.error, invalidMessage);
            return;
        }
        this.showLoading();
        const mode = this.mode;
        const options = {};
        if (mode === "edit")
            options.rowid = edit.dataset.id;
        const cmd = mode === "insert" ? TableServerCmd.save_insert_row : TableServerCmd.update_row;
        const appendValues = encodeValues(edit);
        ViewWidgetHelper.ExecCmd(this.element, {
            cmdType: ViewCmdType.itemcmd,
            cmd,
            options,
            appendValues,
            onSuccess: (data) => {
                this.saveRowComplete(data?.html, data?.footer);
                this.onDataChanged(mode === "insert" ? TableServerEvents.insertrow : TableServerEvents.updaterow, edit.dataset.id);
                this.hideLoading();
            },
            onError: () => {
                console.error('request error');
                this.hideLoading();
            },
            setBusy: false
        });
    }
    /**
     * 2026-10-06: Запускает проверку полей строки редактирования через общую систему Validation.
     * @param edit Строка редактирования (tr._editrow)
     * @returns Текст первой ошибки; общий текст, если у поля нет своего; null, если ошибок нет
     */
    _editRowValidationError(edit) {
        Validation.run(edit.querySelectorAll("[data-widget], input[data-regex], textarea[data-regex]"));
        const invalid = edit.querySelector('[aria-invalid="true"], .input-validation-error');
        if (!invalid)
            return null;
        return invalid.title.trim() || Loc.rowHasInvalidFields;
    }
    cancelEdit() {
        if (this.mode !== "edit") {
            this.currentRow(null);
            this.uncheckRow(null);
        }
        const tbody = this.element.querySelector("tbody");
        if (!tbody)
            return;
        // 1) Найти скрытую исходную строку
        const edited = tbody.querySelector("tr._editedrow");
        // 2) Удалить редактируемую строку (если отмечена _editrow)
        tbody.querySelectorAll("tr._editrow").forEach(tr => tr.remove());
        // 3) На всякий случай удалить ближайший хвост с кнопками
        tbody.querySelectorAll("tr.f").forEach(tr => tr.remove());
        if (edited) {
            edited.classList.remove("_editedrow");
            DomHelpers.show(edited);
            this.currentRow(edited);
        }
        this.hiddenSync.write(Array.from(this.selection.selected), this.selection.focused || null);
        this.refreshState("view");
    }
    openFilterCmd() {
        this.showLoading();
        writeTpk(this.element, this._tableKey, "filterOn", "True");
        if (this.filterAsideInst) {
            this.hideLoading();
            const aside = this.getFormFilter();
            if (aside)
                DomHelpers.show(aside);
            this.mountFilterAside();
            this.refreshState();
            return;
        }
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: TableServerCmd.open_filter,
            cmdType: ViewCmdType.itemcmd,
            onSuccess: (data) => {
                if (data?.formMode) {
                    const wrapper = document.createElement("aside");
                    wrapper.className = "im-modal-aside im-modal-aside-filter modal-aside";
                    wrapper.innerHTML = `
                  <div class="modal-aside__header">
                    <h4 class="modal-aside__title"></h4>
                    <i class="modal-aside__close" data-filter-close></i>
                  </div>
                  <div class="modal-aside__content"></div>`;
                    wrapper.querySelector(".modal-aside__content")
                        .insertAdjacentHTML("beforeend", data.html);
                    this.element.before(wrapper);
                    ImSys.initDom(wrapper); // <— ВАЖНО
                    this.mountFilterAside();
                }
                else {
                    this.filterRow(data?.html);
                }
                this.refreshState();
                this.hideLoading();
            },
            onError: (_err) => {
                writeTpk(this.element, this._tableKey, "filterOn", "False");
                this.hideLoading();
            }
        });
    }
    closeFilterCmd() {
        const aside = this.getFormFilter();
        if (aside) {
            DomHelpers.hide(aside);
            // если на панели есть активные фильтры — на этом останавливаемся
            if (this.cmdPanel && this.cmdPanel.filterCount == 0) {
                writeTpk(this.element, this._tableKey, "filterOn", "False");
            }
        }
        else {
            // очистим строку фильтра (если была)
            this.filterRow(null);
            writeTpk(this.element, this._tableKey, "filterOn", "False");
            this._itemsCtrl.reloadAll();
        }
        this.refreshState();
    }
    openGroup(rowId) {
        let id = rowId;
        if (!id)
            id = this.currentRow()?.dataset.id || "";
        if (!id)
            return;
        if (this.items.folders && this.items.folders.setFolder)
            this.items.folders.setFolder(id);
        // Синхронизация с деревом (как в легаси)
        this.element.dispatchEvent(new CustomEvent("im-table-group-opened", {
            bubbles: true,
            detail: {
                id
            }
        }));
    }
    _onDestroy() {
        this.selAdapter?.destroy();
        this.filterRowInst?.destroy();
        this.filterAsideInst?.destroy();
        this.sortWidget?.destroy();
        this.dispose.forEach((d) => d());
        this.hiddenSync?.dispose();
        this.stackAdapter?.destroy();
    }
    // ──────────────────────────────────────────────────────────────────────────
    // Внутренние процедуры
    // ──────────────────────────────────────────────────────────────────────────
    /**
     * Вставка/замена DOM после полной перезагрузки таблицы.
     * @param html   HTML для tbody
     * @param footer HTML для footer (опционально)
     */
    reloadComplete(html, footer) {
        // перерисовка
        const tbody = this.element.querySelector("tbody");
        if (tbody && typeof html === "string") {
            tbody.innerHTML = html;
            ImSys.initDom(tbody);
        }
        this.updateFooter(footer);
        // восстановить выделение
        this.selAdapter.reconcileAfterReload();
        // Зафиксировать актуальные значения в hidden
        this.hiddenSync.write(Array.from(this.selection.selected), this.selection.focused || null);
        this.hideLoading();
    }
    /** Вставка/замена DOM для строки после reloadRow */
    reloadRowComplete(html, inserted = false, footer) {
        this.updateFooter(footer);
        if (!html)
            return;
        const tbody = this.element.querySelector("tbody");
        if (!tbody)
            return;
        const row = this.createRowFromHtml(html);
        if (!row)
            return;
        const id = row.dataset.id ?? "";
        if (inserted) {
            // Поведение "новая строка": но и тут подстрахуемся от дублей
            if (id) {
                const same = Array.from(tbody.querySelectorAll(`tr[data-id="${id}"]`));
                if (same.length > 0) {
                    same[0].replaceWith(row);
                    for (let i = 1; i < same.length; i++)
                        same[i].remove();
                }
                else {
                    tbody.appendChild(row);
                }
            }
            else {
                tbody.appendChild(row);
            }
            ImSys.initDom(row);
            this.uncheckRow(null);
            this.currentRow(row);
            this.scrollToRow(row);
            return;
        }
        // Обновление существующей строки: заменить первую, остальные удалить
        if (id) {
            const same = Array.from(tbody.querySelectorAll(`tr[data-id="${id}"]`));
            if (same.length > 0) {
                same[0].replaceWith(row); // сохраняем позицию
                for (let i = 1; i < same.length; i++)
                    same[i].remove();
            }
            else {
                tbody.appendChild(row);
            }
        }
        else {
            tbody.appendChild(row);
        }
        ImSys.initDom(row);
    }
    // 1) Вынеси это в хелпер внутри TableWidget:
    getColumnsCount(tbody) {
        const table = tbody.closest('table');
        if (!table)
            return 1;
        // приоритет — <colgroup>/<col>
        const byColgroup = table.querySelectorAll('col').length;
        if (byColgroup > 0)
            return byColgroup;
        // иначе считаем колонки по первой строке thead
        const headRow = table.tHead?.rows?.[0];
        if (headRow) {
            return Array.from(headRow.cells).reduce((n, c) => n + (c.colSpan || 1), 0);
        }
        // в крайнем случае — по первой строке tbody
        const bodyRow = tbody.querySelector('tr');
        if (bodyRow) {
            return Array.from(bodyRow.cells).reduce((n, c) => n + (c.colSpan || 1), 0);
        }
        return 1;
    }
    /**
     * Завершает переход таблицы в режим **inline-вставки** новой строки.
     *
     * Вставляет форму новой строки в конец <tbody> и переводит виджет в режим 'insert'.
     * Добавляет HTML формы + служебную строку <tr.f>, помечает основную строку как ._editrow,
     * инициализирует через OnHtmlAdded, скроллит к форме и вешает обработчики кнопок.
     * @param html HTML-фрагмент формы (<tr>); если пусто — выход без действий.
     */
    insertRowInlineComplete(html) {
        this.currentRow(null);
        this.uncheckRow(null);
        const tbody = this.element.querySelector('tbody');
        if (!tbody || !html)
            return;
        const cols = this.getColumnsCount(tbody); // <- вместо цепочки querySelector/closest
        const block = `${html}<tr class='f'><td colspan='${cols}'><div class='formbuttons'>
<button class='btn im-formbutton' data-type='save-row'>${Loc.save}</button>
<button class='btn btp2 im-formbutton' data-type='cancel-edit-row'>${Loc.cancel}</button>
</div></td></tr>`;
        tbody.insertAdjacentHTML('beforeend', block);
        const fRow = tbody.querySelector('tr.f:last-of-type');
        const editRow = fRow?.previousElementSibling;
        if (editRow) {
            editRow.classList.add('_editrow');
            ImSys.initDom(editRow);
            this.scrollToRow(editRow);
        }
        this.refreshState('insert');
        this._bindEditRowButtons();
    }
    /**
     * Открывает форму вставки строки.
     *
     * - Отображает форму в aside, во вкладке панели или в диалоге.
     * - Инициализирует представление в режиме insert.
     * - Подписывается на события вставки/сохранения для обновления строки.
     * - Обновляет состояние вида.
     *
     * @param data Параметры формы (html, title, width, height, css, mainTabs, mode)
     */
    insertRowFormComplete(data) {
        const { html, title, width, height, css, mainTabs, mode } = data || {};
        // Без внешней обертки: берем реальный корень view
        const tmp = document.createElement("div");
        tmp.innerHTML = html || "";
        const root = tmp.firstElementChild;
        if (!root)
            return;
        if (mode === "aside") {
            this.ensureAsideWithContent(title, root);
        }
        else if (!this.element.closest(".im-panel") || mainTabs === false) {
            DialogComponent.show({
                title,
                content: root,
                width,
                height,
                dialogClass: css,
                buttons: [{ text: Loc.close }]
            });
        }
        else {
            const panel = this.element.closest(".im-panel");
            if (panel)
                TabsPanelWidget.getOrInit(panel).addTab({ title, element: root, description: "", viewChanges: true });
        }
        ViewWidget.getOrInit(root, { mode: ViewMode.insert });
        root.addEventListener("oninsert", (e) => {
            const rowID = e?.detail?.rowID;
            const andClose = e?.detail?.andClose;
            if (andClose !== true)
                root.addEventListener("onsave", () => this._itemsCtrl.reload(rowID));
            this._itemsCtrl.reload(rowID, true);
        });
        this.refreshState("view");
    }
    /**
     * Переводит строку таблицы в режим inline-редактирования и вставляет блок формы ниже.
     *
     * - Скрывает редактируемую строку и добавляет под ней HTML формы и кнопки действий.
     * - Инициализирует добавленный HTML.
     * - Скроллит к форме, обновляет состояние и привязывает обработчики кнопок.
     *
     * @param row  Редактируемая строка таблицы (tr)
     * @param html HTML формы (опционально)
     */
    editRowInlineComplete(row, html) {
        row.classList.add("_editedrow");
        DomHelpers.hide(row);
        const tbody = this.element.querySelector('tbody');
        if (!tbody)
            return;
        tbody.querySelectorAll("tr._editrow, tr.f").forEach(tr => tr.remove());
        const cols = this.getColumnsCount(tbody);
        const block = `${html || ""}<tr class='f'><td colspan='${cols}'><div class='formbuttons'>
<button class='btn btp1 im-formbutton' data-type='save-row'>${Loc.save}</button>
<button class='btn btp2 im-formbutton' data-type='cancel-edit-row'>${Loc.cancel}</button>
    </div></td></tr>`;
        row.insertAdjacentHTML("afterend", block);
        const editBlock = row.nextElementSibling;
        if (editBlock) {
            editBlock.classList.add("_editrow");
            ImSys.initDom(editBlock); // вместо this.element
            this.scrollToRow(editBlock);
        }
        this.refreshState("edit");
        this._bindEditRowButtons();
    }
    /**
     * Открывает форму редактирования связанную со выбранной строкой таблицы.
     *
     * - В зависимости от режима отображает содержимое в aside, во вкладке панели или в диалоге.
     * - Навешивает обработчик события "onsave" для перезагрузки строки.
     * - При необходимости автоматически переключает форму в режим редактирования.
     * - Скрывает индикатор загрузки и обновляет состояние.
     *
     * @param row          HTML-элемент строки таблицы
     * @param data         Параметры формы (html, title, width, height, css, mainTabs, mode)
     * @param viewEditMode Нужно ли сразу открыть режим редактирования внутри aside
     */
    editRowFormComplete(row, data, viewEditMode) {
        const { html, title, width, height, css, mainTabs, mode } = data || {};
        // 1) Парсим html и стараемся не добавлять обертку
        const tmp = document.createElement("div");
        tmp.innerHTML = html || "";
        // Ожидается что это именно div от view
        const root = tmp.firstElementChild;
        if (mode === "aside") {
            // возможно, стоит учесть режимы edit/insert внутри aside, как в старом коде
            this.ensureAsideWithContent(title, root);
            this._asideRowView = root;
        }
        else if (!this.element.closest(".im-panel") || mainTabs === false) {
            // для DialogComponent по-прежнему безопаснее отдать строковый html
            DialogComponent.show({
                title,
                content: root,
                width, height,
                dialogClass: css,
                buttons: [{ text: Loc.close }]
            });
        }
        else {
            const panel = this.element.closest(".im-panel");
            if (panel)
                TabsPanelWidget.getOrInit(panel).addTab({
                    title, element: root, description: "", viewChanges: true
                });
        }
        // 3) Инициализируем именно корневой узел view
        ViewWidget.getOrInit(root);
        // 4) Подписка на сохранение
        const rowId = row.getAttribute("data-id");
        if (rowId) {
            root.addEventListener("onsave", () => this._itemsCtrl.reload(rowId));
        }
        // 5) Авто-открытие формы редактирования для aside
        if (mode === "aside" && viewEditMode) {
            root.querySelector(".bt-edit")
                ?.dispatchEvent(new MouseEvent("click", { bubbles: true }));
        }
        // 6) Вернуть скрытие индикатора загрузки (как в старом коде)
        this.hideLoading?.();
        this.refreshState("view");
    }
    createRowFromHtml(html) {
        if (!html)
            return null;
        const tb = document.createElement('tbody');
        tb.innerHTML = html.trim();
        return tb.firstElementChild;
    }
    /** Обновить DOM после сохранения строки (insert/update) */
    saveRowComplete(html, footer) {
        this.updateFooter(footer);
        const tbody = this.element.querySelector("tbody");
        if (!tbody || !html)
            return;
        const newRowEl = this.createRowFromHtml(html);
        if (!newRowEl)
            return;
        const id = newRowEl.dataset.id ?? "";
        // 1) Убрать хвосты формы прежде всего
        tbody.querySelectorAll("tr._editrow, tr.f").forEach(tr => tr.remove());
        // 2) Дедупликация по data-id: заменить первую копию, остальные удалить
        if (id) {
            const same = Array.from(tbody.querySelectorAll(`tr[data-id="${id}"]`));
            if (same.length > 0) {
                // Сохраняем позицию строки
                same[0].replaceWith(newRowEl);
                for (let i = 1; i < same.length; i++)
                    same[i].remove();
            }
            else {
                tbody.appendChild(newRowEl);
            }
        }
        else {
            // На всякий случай, если пришёл HTML без id
            tbody.appendChild(newRowEl);
        }
        // 3) Инициализация и состояние
        ImSys.initDom(newRowEl);
        this.currentRow(newRowEl);
        this.scrollToRow(newRowEl, 0);
        this.hiddenSync.write(Array.from(this.selection.selected), this.selection.focused || null);
        this.refreshState("view");
    }
    /** Обновить существующий footer без изменения тела таблицы. */
    updateFooter(footer) {
        const tfoot = this.element.querySelector("tfoot");
        if (!tfoot || typeof footer !== "string")
            return;
        tfoot.innerHTML = footer;
        ImSys.initDom(tfoot);
    }
    ensureAsideWithContent(title, view) {
        let aside = this.getAsideForm();
        if (!aside) {
            aside = document.createElement("aside");
            aside.className = "im-modal-aside modal-aside";
            aside.innerHTML = `
          <div class="modal-aside__header">
            <h4 class="modal-aside__title"></h4>
            <i class="modal-aside__close" data-filter-close></i>
          </div>
          <div class="modal-aside__content"></div>`;
            // 2026-09-12: панель карточки живёт в body, как и панель обычного openAside.
            // Внутри main она попадала в его контекст наложения и уходила под меню,
            // а два места поиска панели расходились между собой.
            document.body.appendChild(aside);
        }
        aside.querySelector(".modal-aside__title").textContent = title || "";
        const content = aside.querySelector(".modal-aside__content");
        content.innerHTML = "";
        content.appendChild(view);
        ImSys.initDom(aside);
        DomHelpers.show(aside);
    }
    getAsideForm() {
        // 2026-09-12: панель карточки одна на документ, поэтому ищем её так же,
        // как DefaultUiCommandProvider.openAside. Панель фильтра остаётся своей у
        // каждой таблицы и из поиска исключена.
        const aside = document.querySelector('.im-modal-aside:not(.im-modal-aside-filter)');
        return aside;
    }
    getFormFilter() {
        const sibling = this.element.previousElementSibling;
        const beforeSibling = sibling?.previousElementSibling;
        if (sibling) {
            if (sibling.classList.contains('im-modal-aside-filter')) {
                return sibling;
            }
            else if (beforeSibling && beforeSibling.classList.contains('im-modal-aside-filter')) {
                return beforeSibling;
            }
        }
        return null;
    }
    mountFilterAside() {
        const aside = this.getFormFilter();
        if (!aside)
            return;
        this.filterAsideInst?.destroy();
        this.filterAsideInst = FilterWidget.getOrInit(aside, {
            ownerHost: this.element,
            chipsHost: this.ensureChipsHost(),
            onFilterClose: () => {
                this.refreshState();
            },
            closeOnOutsideClick: true
        });
    }
    /**
     * Управляет строкой фильтра таблицы.
     *
     * - При html === null удаляет строку фильтра.
     * - При переданном html вставляет строку фильтра в thead, инициализирует FilterWidget.
     */
    filterRow(html) {
        const thead = this.element.querySelector("thead");
        if (!thead)
            return;
        const prev = thead.querySelector(".im-table-filter");
        if (prev)
            prev.remove();
        // Если html не передан, значит подразумевалось удаление фильтра
        if (html === null) {
            this.filterRowInst?.destroy();
            this.filterRowInst = undefined;
            return;
        }
        // Вставляем СТРОКУ html прямо в thead, как в старом коде
        if (this.element.classList.contains("filter-above")) {
            thead.insertAdjacentHTML("afterbegin", html || "");
        }
        else {
            thead.insertAdjacentHTML("beforeend", html || "");
        }
        const row = thead.querySelector(".im-table-filter");
        if (!row)
            return;
        ImSys.initDom(row);
        {
            this.filterRowInst?.destroy?.();
            this.filterRowInst = FilterWidget.getOrInit(row, {
                ownerHost: this.element,
                chipsHost: this.ensureChipsHost(),
                onFilterClose: () => {
                    this.closeFilterCmd();
                },
                closeOnOutsideClick: false
            });
        }
    }
    ensureChipsHost() {
        let chips = this.element.querySelector(".chips");
        if (!chips) {
            const wrap = document.createElement("div");
            wrap.className = "selected-filters";
            wrap.innerHTML = '<div class="chips"></div>';
            const ts = this.element.querySelector(".im-toolstrip");
            (ts ?? this.element).insertAdjacentElement("afterend", wrap);
            chips = wrap.querySelector(".chips");
        }
        return chips;
    }
    execCustomBtn(el) {
        if (!el || el.classList.contains("off"))
            return;
        this.showLoading();
        const options = { rowindexes: this.checkedRowsIndexes() };
        const noreload = el.classList.contains("noreload");
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: el.getAttribute("data-cmd") || TableServerCmd.custom,
            options,
            onSuccess: () => { if (!noreload)
                this._itemsCtrl.reloadAll();
            else
                this.hideLoading(); },
            onError: () => { console.error('request error'); this.hideLoading(); }
        });
    }
    doDelMark() { this._itemsCtrl.execRowsCmd("delmark-rows", false); }
    doDeleteFolder() {
        this.showLoading();
        const groupId = readTpk(this.element, this._tableKey, "groupID") || "";
        const indexes = this.options.treemanagment ? (groupId || "") : this.checkedRowsIndexes();
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: TableServerCmd.delete_folders,
            options: { rowindexes: indexes },
            onSuccess: () => this._itemsCtrl.reloadAll(),
            onError: () => { console.error('request error'); this.hideLoading(); }
        });
    }
    changeGroup() {
        const selCount = this.checkedRows().length;
        if (selCount === 0) {
            ImSys.alert(Loc.groupSelect, Loc.noItemsSelectedToMove);
            return;
        }
        const ids = this.checkedRowsIndexes();
        ImSys.alertConfirm(Loc.groupSelect, `${Loc.selected} <b>${selCount}</b> ${Loc.items}.<br/>${Loc.selectGroupWhichYouWantToMoveItems_Click} "${Loc.confirm}"`, () => {
            this.showLoading();
            const options = {
                itemsToMove: ids,
                targetGroup: this.element.querySelector("input._gr")?.value
            };
            ViewWidgetHelper.ExecCmd(this.element, {
                cmd: TableServerCmd.change_group,
                options,
                onSuccess: () => this._itemsCtrl.reloadAll(ids.split(',')),
                onError: () => { console.error('request error'); this.hideLoading(); }
            });
        }, undefined, "input", false);
    }
    editGroup() {
        const folderUI = readTpk(this.element, this._tableKey, "groupID");
        const tableKey = this._tableKey;
        const tree = this.element.closest(".im-view")?.querySelector(".im-tree");
        const treeKey = tree?.getAttribute("data-key");
        const row = this.currentRow();
        const id = row?.querySelector(".r-ic.group") ? row.dataset.id : folderUI;
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: TableServerCmd.open_edit_folder,
            options: { folderUI: id, tableKey, treeKey }
        });
    }
    /** Открытие диалога для ввода названия новой группы
     * и добавление группы после подтверждения
     */
    insertGroup() {
        const inputId = `new-group-name-${this._tableKey}-${Date.now()}`;
        ImSys.alertConfirm(Loc.createNewGroup, `${Loc.newGroupName}:<br/><input id="${inputId}" type="text" style="width:80%;" />`, (dialog) => {
            const input = dialog?.shadowRoot?.getElementById(inputId);
            const title = input?.value?.trim() || "";
            this.startInsertGroup(title);
        }, undefined, "input");
    }
    startInsertGroup(groupTitle) {
        if (!groupTitle) {
            ImSys.alert(Loc.error, Loc.titleRequired);
            return;
        }
        this.showLoading();
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: TableServerCmd.insert_group,
            options: { groupTitle },
            onSuccess: (data) => {
                this.saveRowComplete(data?.html);
                this.onDataChanged(TableServerEvents.insertgroup);
                const view = this.element.closest(".im-view");
                const tree = view?.querySelector(".im-tree");
                const key = tree?.getAttribute("data-key");
                if (view && key)
                    ViewWidgetHelper.reload(view, { itemKeysToReload: key });
                this.hideLoading();
            },
            onError: () => { console.error('request error'); this.hideLoading(); }
        });
    }
    checkedRows() {
        const ids = Array.from(this.selection?.selected || []);
        return ids.map((id) => this.getRow(id)).filter(Boolean);
    }
    checkedRowsIndexes() { return Array.from(this.selection?.selected || []).join(","); }
    currentRow(row) {
        if (row === undefined) {
            const id = this.selection?.focused || null;
            return id ? this.getRow(id) : null;
        }
        if (row === null) {
            this.selection?.clear?.();
            return null;
        }
        const id = row.dataset.id;
        if (id)
            this.selection.set([id]);
        return row;
    }
    uncheckRow(_row) { this.selection.clear(); }
    getRow(rowID) {
        return this.element.querySelector(`tbody tr[data-id="${rowID}"]`);
    }
    emitCheckedChanged(ids) {
        const on = this.options.oncheckedchanged ?? null;
        const rowIndexes = ids.join(",");
        if (on !== null) {
            const cmd = on === "true" ? TableServerCmd.checked_rows_changed : on;
            ViewWidgetHelper.ExecCmd(this.element, {
                cmd,
                options: { rowIndexes }
            });
        }
        if (this.options.imoncheckedchanged) {
            ViewWidgetHelper.execCmdString(this.element, this.element, this.options.imoncheckedchanged);
        }
    }
    resolveCellClickCommand(value) {
        if (typeof value !== 'string') {
            if (value === true) {
                console.error('TableWidget: data-oncellclick requires an explicit command name');
            }
            return undefined;
        }
        const command = value.trim();
        if (!command || command.toLowerCase() === 'true') {
            console.error('TableWidget: data-oncellclick requires an explicit command name');
            return undefined;
        }
        return command;
    }
    execCellClick(payload) {
        const cmd = this.cellClickCommand;
        if (!cmd || this.cellClickPending)
            return;
        this.cellClickPending = true;
        const complete = () => {
            this.cellClickPending = false;
        };
        try {
            // 2026-08-16. false — команда не запущена и колбэков не будет,
            // иначе флаг заблокировал бы таблицу до перезагрузки
            const started = ViewWidgetHelper.ExecCmd(this.element, {
                cmd,
                options: payload,
                onSuccess: complete,
                onError: () => {
                    console.error('request error');
                    complete();
                }
            });
            if (!started)
                complete();
        }
        catch (error) {
            complete();
            throw error;
        }
    }
    isFormModePreferred() {
        const onPanel = this.cmdPanel?.isFormModeOn?.() === true;
        const legacy = this.allows("formEdit") && !this.allows("rowEdit");
        return onPanel || legacy;
    }
    /**
     * Актуализация ширины контейнера панели пагинации.
     * Вызывается после изменений в разметке таблицы.
     */
    setTablePagination() {
        // актуализация ширины контейнера панели — упрощённо
        const cont = this.element.querySelector(".im-toolstrip__container");
        if (!cont)
            return;
        cont.style.removeProperty("width");
        const parent = DomHelpers.closestScrollParent(this.element) || this.element.parentElement;
        if (parent && this.element.clientWidth > parent.clientWidth) {
            cont.style.width = `${parent.clientWidth - 18}px`;
        }
    }
    _bindEditRowButtons() {
        if (this._binded)
            return;
        this._binded = true;
        const onClick = (e) => {
            const t = e.target;
            const btn = t.closest("button.im-formbutton,[data-type]");
            if (!btn)
                return;
            e.preventDefault();
            const type = btn.getAttribute("data-type");
            switch (type) {
                case "save-row":
                    this.saveRow();
                    break;
                case "cancel-edit-row":
                    this.cancelEdit();
                    break;
            }
        };
        this.element.addEventListener("click", onClick, { passive: false });
        this.dispose.push(() => this.element.removeEventListener("click", onClick));
    }
    /**
     * Делает «текущей» строку по id (и при необходимости добавляет к выделению).
     * @param id    data-id строки
     * @param opts  append — добавлять к выделению; scroll — прокрутить к строке
     */
    currentRowByID(id, opts) {
        if (this.selAdapter?.setCurrentById(id, { scroll: true, ...opts })) {
            this.refreshState();
        }
    }
    scrollToRow(rowEl, duration) {
        const area = this.element.querySelector(".scroll-area") || null;
        DomHelpers.scrollTo(area, rowEl, { duration: duration ?? 100, axis: "y", offset: -100 });
    }
    /** Уведомление об изменении данных таблицы */
    onDataChanged(eventType, indexes) {
        const on = this.options.onchanged ?? null;
        if (on !== null) {
            const cmd = on === "true" ? TableServerCmd.table_onchanged : on;
            ViewWidgetHelper.ExecCmd(this.element, {
                cmd,
                options: { eventType, indexes }
            });
        }
    }
    /** Показать индикатор загрузки */
    showLoading() {
        this.element.querySelector(".loading-container")?.classList.add("on");
        this.cmdPanel?.setBusy?.(true);
        this.refreshState();
    }
    /** Скрыть индикатор загрузки */
    hideLoading() {
        this.element.querySelector(".loading-container")?.classList.remove("on");
        this.cmdPanel?.setBusy?.(false);
        this.refreshState();
    }
    /**
     * Обновить состояние таблицы
     * @param newMode Новый режим (опционально)
     */
    refreshState(newMode) {
        if (newMode)
            this.mode = newMode;
        const isLoading = this.element.querySelector(".loading-container")?.classList.contains("on") === true;
        const mode = isLoading ? "loading" : (this.mode || "view");
        // Кнопки внутри форменных строк
        this.element.querySelectorAll("button[data-type]").forEach((b) => {
            if (mode === "loading")
                b.disabled = true;
            else
                b.disabled = false;
        });
        // состояние для панели
        if (this.cmdPanel) {
            const curp = getTpk(this.element, this._tableKey, "curPage");
            const page = curp ? parseInt(curp.value || "1", 10) : 1;
            const hasPagination = !!curp;
            const filterOn = (readTpk(this.element, this._tableKey, "filterOn") === "True");
            const groupVal = readTpk(this.element, this._tableKey, "groupID") || "";
            const hasGroupId = !!(groupVal && groupVal !== "0" && groupVal !== emptyGuid);
            const filterPanelOpen = !!(this.element.querySelector("thead .im-table-filter") ||
                (this.getFormFilter() && DomHelpers.isVisible(this.getFormFilter())));
            this.cmdPanel.setState?.({
                mode,
                hasCurrent: !!this.currentRow(),
                selectedCount: this.checkedRows().length,
                filterOn,
                filterPanelOpen,
                page,
                isRowFolder: !!this.isRowFolder,
                hasGroupId,
                hasPagination,
            });
        }
    }
    /** Публичный API: установить флаг «текущая строка — папка» и обновить панель */
    setTreeFolderState(isFolder) {
        this.isRowFolder = !!isFolder;
        this.refreshState();
    }
    getFilterAdapter() {
        return new TableFilterAdapter(this.element);
    }
}
/** Имя виджета */
TableWidget._alias = "Table";

export { TableWidget };
